import { Select, Spin } from "antd";
import type { SelectProps } from "antd/es/select";
import { debounce } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";

interface Props<ValueType = any>
  extends Omit<SelectProps<any>, "options" | "children"> {
  fetchOptions: (search: string) => Promise<ValueType[]>;
  debounceTimeout?: number;
}

export default function SearchSelect<
  ValueType extends {
    key?: string;
    label: string;
    value: any;
  } = any
>({ fetchOptions, debounceTimeout = 800, value, ...props }: Props<ValueType>) {
  const [fetching, setFetching] = useState(false);
  const [options, setOptions] = useState<ValueType[]>([]);
  const fetchRef = useRef(0);

  const loadOptions = (value: string) => {
    fetchRef.current += 1;
    const fetchId = fetchRef.current;
    setOptions([]);
    setFetching(true);
    fetchOptions(value).then((newOptions) => {
      if (fetchId !== fetchRef.current) {
        // for fetch callback order
        return;
      }

      setOptions(newOptions);
      setFetching(false);
    });
  };

  useEffect(() => {
    loadOptions("");
  }, []);

  const debounceFetcher = useMemo(() => {
    return debounce(loadOptions, debounceTimeout);
  }, [fetchOptions, debounceTimeout]);

  return (
    <Select
      filterOption={false}
      onSearch={debounceFetcher}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      showSearch
      onDropdownVisibleChange={(open) => !open && loadOptions("")}
      {...props}
      // @ts-ignore
      value={options.find((option) => option.value === value)?.label}
      options={options}
    />
  );
}
